import { Component, OnDestroy, OnInit } from '@angular/core';
import { Analysis } from '../interfaces/analysis';
import { AnalysisListItemComponent } from '../shared/analysis-list-item/analysis-list-item.component';
import { MatListModule } from '@angular/material/list';
import { CommonModule } from '@angular/common';
import { secondaryListItemConfig } from '../interfaces/secondaryListItemConfig';
import { SecondaryListItemComponent } from '../shared/secondary-list-item/secondary-list-item.component';
import { AnalysisService } from '../services/analysis-tab/analysis.service';
import { SampletestService } from '../services/sampletest/sampletest.service';
import { InstrumentRunService } from '../services/instrument-run/instrument-run.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SidebarService } from '../services/sidebar-btn/sidebar.service';
import { MessageModalService } from '../services/message-modal/message-modal.service';
import { InstrumentRun } from '../interfaces/instrument-run';
import { SampleTestInterface } from '../interfaces/sample-test-panel';
import { ButtonsComponent } from '../shared/buttons/buttons.component';
import { Router } from '@angular/router';
import { NewsService } from '../services/news/news.service';
import { NewsItem } from '../interfaces/news';
import { NewsCardComponent } from '../shared/news-card/news-card/news-card.component';
import { AuthService } from '../services/auth/auth.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    SecondaryListItemComponent,
    AnalysisListItemComponent,
    MatListModule,
    CommonModule,
    MatProgressSpinnerModule,
    ButtonsComponent,
    NewsCardComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class HomeComponent implements OnInit, OnDestroy {
  isLoading: boolean = true;
  hasNewsData: boolean = false;
  btnNameOne: string = 'Instrument Run';
  btnNameTwo: string = 'Fasta File';
  btnMatFlat: string = 'mat-flat-button';
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';

  // Data properties
  analysisData: Analysis[] = [];
  sampleData: SampleTestInterface[] = [];
  instrumentRunData: InstrumentRun[] = [];
  newsData: NewsItem[] = [];

  public sampleConfig: secondaryListItemConfig = {
    name: 'SampleName',
    userName: 'UserName',
    date: 'CreatedOn',
    id: 'SampleID',
    entityType: 'Sample',
  };

  public instrumentConfig: secondaryListItemConfig = {
    name: 'Name',
    userName: 'UserName',
    date: 'UploadedOn',
    id: 'InstrumentRunID',
    entityType: 'Instrument Run',
  };
  private destroy$ = new Subject<void>();

  constructor(
    private analysisService: AnalysisService,
    private instrumentService: InstrumentRunService,
    private sampleService: SampletestService,
    private sidebarService: SidebarService,
    private newsService: NewsService,
    private messageModal: MessageModalService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.sidebarService.open();
    this.authService.authUserData$.pipe(takeUntil(this.destroy$)).subscribe((authData) => {
      if (authData) this.loadData();
    });
  }

  async loadData() {
    this.isLoading = true;

    try {
      // Convert observables to promises
      const analysisPromise = firstValueFrom(this.analysisService.getAnalysisForHomePage());
      const samplesPromise = firstValueFrom(this.sampleService.getSamplesForHomePage());
      const instrumentRunPromise = firstValueFrom(this.instrumentService.getInstrumentRunForHomePage());
      const newsPromise = firstValueFrom(this.newsService.getNews());

      const [analysisResponse, sampleResponse, instrumentRunResponse, newsResponse] = await Promise.all([
        analysisPromise,
        samplesPromise,
        instrumentRunPromise,
        newsPromise,
      ]);

      this.analysisData = analysisResponse;
      this.sampleData = sampleResponse;
      this.instrumentRunData = instrumentRunResponse;

      // ORIGINAL -------
      if (newsResponse && newsResponse.success && newsResponse.data) {
        this.newsData = newsResponse.data;
        this.hasNewsData = this.newsData.length > 0;
      }

      // --- BOILERPLATE / MOCK DATA LOGIC ---
      // if (newsResponse && newsResponse.success && newsResponse.data && newsResponse.data.length > 0) {
      //   const realItem = newsResponse.data[0];
      //   this.newsData = Array(9).fill(realItem);
      //   this.hasNewsData = this.newsData.length > 0;
      // }
      // -------------------------------------
    } catch (err) {
      this.messageModal.openModal($localize`Error`, $localize`Failed to load data.`);
    } finally {
      this.isLoading = false;
    }
  }

  get homePageData(): boolean {
    return !(this.analysisData.length === 0 && this.sampleData.length === 0 && this.instrumentRunData.length === 0);
  }

  redirectToInstrumentRun(): void {
    this.router.navigateByUrl('/instrument-run');
  }

  redirectToFastaFile(): void {
    this.router.navigateByUrl('/file-management');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
